import React, { useMemo } from 'react';
import KPICard, { KPIGrid } from '../KPICard';
import BarChart from '../charts/BarChart';
import LineChart from '../charts/LineChart';
import DataTable from '../DataTable';
import { formatCurrency, formatFixed, formatInt, formatPct, formatUnitPrice } from '../../utils/format';
import {
  openDrillSelection,
  selectionFromDiscRateMark,
  selectionFromPriceDeckMark,
  selectionFromWellMark,
} from '../../utils/drilldown';
import { useDeal } from '../../deals/DealContext';
import {
  isOilPrimary,
  resolveDifferential,
  resolvePriceDeckSeries,
  shouldShowGasVolumes,
} from '../../utils/commodityDisplay';

function netVolumeSub(mbbl, bcf, oilSuffix = 'Mbbl net', gasSuffix = 'Bcf net') {
  const parts = [];
  if (mbbl != null) parts.push(`${mbbl} ${oilSuffix}`);
  if (bcf != null) parts.push(`${bcf} ${gasSuffix}`);
  return parts.length ? parts.join(' · ') : '—';
}

export default function Reserves({ data }) {
  const { deal } = useDeal();
  const dealOrMeta = deal || data?.meta;
  const isOil = isOilPrimary(dealOrMeta);
  const { summary, priceDeck, differentials, wellsByPv10, pbpCandidates, pvSensitivity } = data;
  const top = wellsByPv10.slice(0, 20);
  const showGasVol = shouldShowGasVolumes(dealOrMeta, wellsByPv10, ['netGasMmcf']);
  const drillWell = (d) => openDrillSelection(selectionFromWellMark(d), { path: 'reserves' });
  const drillDisc = (d) => openDrillSelection(selectionFromDiscRateMark(d), { path: 'reserves' });
  const drillDeck = (d) => openDrillSelection(selectionFromPriceDeckMark(d), { path: 'reserves' });

  const deckSeries = useMemo(
    () => resolvePriceDeckSeries(priceDeck, dealOrMeta),
    [priceDeck, dealOrMeta],
  );

  const wellVolumeColumns = useMemo(() => {
    const cols = [];
    if (isOil) {
      cols.push({ key: 'netOilMbbl', label: 'Net Mbbl', format: formatInt });
    }
    if (showGasVol) {
      cols.push({ key: 'netGasMmcf', label: 'Net MMcf', format: formatInt });
    }
    if (!cols.length) {
      cols.push({ key: 'netGasMmcf', label: 'Net MMcf', format: formatInt });
    }
    return cols;
  }, [isOil, showGasVol]);

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Reserves & Economics</h2>
          <p className="page-subtitle">
            Seller case · {data.meta.priceDeck} · eff. {data.meta.effectiveDate}
          </p>
          <div className="page-quote">
            <span className="page-quote-label">{data.quoteLabel}</span>
            <span>&ldquo;{data.quote}&rdquo;</span>
          </div>
        </div>
      </div>

      <KPIGrid>
        <KPICard
          label="PDP PV10"
          value={formatCurrency(summary.pdpPv10MM != null ? summary.pdpPv10MM * 1e6 : null)}
          sub={netVolumeSub(summary.pdpNetMbbl, summary.pdpNetBcf)}
          color="var(--amber)"
        />
        <KPICard
          label="PBP PV10"
          value={formatCurrency(summary.pbpPv10MM != null ? summary.pbpPv10MM * 1e6 : null)}
          sub={
            summary.pbpNetMbbl != null
              ? `${summary.pbpNetMbbl} Mbbl`
              : summary.pbpNetBcf != null
                ? `${summary.pbpNetBcf} Bcf · $35K/well`
                : '—'
          }
          color="var(--teal)"
        />
        <KPICard
          label="Total PV10"
          value={formatCurrency(summary.totalPv10MM != null ? summary.totalPv10MM * 1e6 : null)}
          sub={`Undisc. PDP CF ${formatCurrency(summary.undiscPdpMM != null ? summary.undiscPdpMM * 1e6 : null)}`}
          color="var(--green)"
        />
        <KPICard
          label="Economic life"
          value={summary.lifeYears != null ? `${summary.lifeYears} yrs` : '—'}
          sub="Primary discount 10%"
          color="var(--navy)"
        />
      </KPIGrid>

      <section className="dashboard-section">
        <div className="charts-grid charts-grid-2">
          <div className="chart-card">
            <div className="chart-title">PV sensitivity by discount rate</div>
            <div className="chart-subtitle">PDP vs total package ($MM) · click point for detail</div>
            <LineChart
              data={pvSensitivity}
              xKey="rate"
              series={[
                { key: 'pdpMM', label: 'PDP', color: '#1B355C', area: true },
                { key: 'totalMM', label: 'PDP+PBP', color: '#F59E0B', area: false },
              ]}
              xType="number"
              yFormat={(v) => (v == null || Number.isNaN(Number(v)) ? '—' : `$${Number(v).toFixed(1)}M`)}
              pointLabel
              onPointClick={drillDisc}
            />
          </div>
          <div className="chart-card">
            <div className="chart-title">
              {deckSeries.yKey === 'wti' ? 'WTI strip price deck' : 'NYMEX strip price deck'}
            </div>
            <div className="chart-subtitle">{deckSeries.subtitle}</div>
            <BarChart
              data={priceDeck}
              xKey="year"
              yKey={deckSeries.yKey}
              yFormat={(v) => formatUnitPrice(v, '')}
              color="#F59E0B"
              barLabel
              onBarClick={drillDeck}
            />
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-title">Differentials & shrink (model inputs)</div>
        <div className="charts-grid charts-grid-2">
          {differentials.map((d) => {
            const diff = resolveDifferential(d);
            return (
              <div key={d.group} className="chart-card">
                <div className="chart-title">{d.group}</div>
                <div className="kpi-value" style={{ fontSize: '1.8rem', color: 'var(--amber)' }}>
                  {formatUnitPrice(diff.value, diff.unit)}
                </div>
                <div className="chart-subtitle">
                  {d.shrinkPct != null
                    ? `Shrink remaining ${formatPct(d.shrinkPct)}`
                    : d.note || (isOil ? 'Oil differential' : '—')}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-title">Behind-pipe recompletion candidates</div>
        <DataTable
          columns={[
            { key: 'well', label: 'Well' },
            { key: 'zones', label: 'Zones' },
            { key: 'netMmcf', label: 'Net MMcf', format: formatInt },
            { key: 'pv10M', label: 'PV10 ($M)', format: (v) => (v == null ? '—' : `$${formatFixed(v, 0)}`) },
            { key: 'capexK', label: 'Capex ($K)', format: (v) => (v == null ? '—' : `$${v}`) },
            { key: 'note', label: 'Note' },
          ]}
          data={pbpCandidates}
          onRowClick={(row) => drillWell({ lease: row.well, api: row.api, propnum: row.propnum })}
        />
      </section>

      <section className="dashboard-section">
        <div className="section-title">Well-level oneline (top 20 by PV10)</div>
        <div className="chart-card" style={{ marginBottom: 16 }}>
          <BarChart
            data={top.map((w) => ({ ...w, pv10MM: w.pv10M / 1000 }))}
            xKey="lease"
            yKey="pv10MM"
            horizontal
            yFormat={(v) => formatCurrency(v * 1e6)}
            color={(d) => (d.resCat === 'PBP' ? '#14B8A6' : d.pv10M < 0 ? '#DC2626' : '#1B355C')}
            barLabel
            onBarClick={drillWell}
          />
        </div>
        <DataTable
          columns={[
            { key: 'lease', label: 'Lease' },
            { key: 'resCat', label: 'Cat' },
            { key: 'wellType', label: 'Type' },
            { key: 'wi', label: 'WI %', format: (v) => formatFixed(v, 1) },
            { key: 'nri', label: 'NRI %', format: (v) => formatFixed(v, 1) },
            ...wellVolumeColumns,
            { key: 'pv10M', label: 'PV10 ($M)', format: (v) => formatFixed(v, 1) },
            { key: 'undiscCfM', label: 'Undisc CF ($M)', format: (v) => formatFixed(v, 1) },
          ]}
          data={wellsByPv10}
          defaultSort="pv10M"
          onRowClick={drillWell}
        />
      </section>
    </div>
  );
}
